import { useFormContext, useWatch } from 'react-hook-form';
import { Element as TargetToScroll } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { PricingProductOptionsGroup } from '../../../../../../store/features/products/types';
import { PricingPackagesFormValues } from '../../../../../../store/features/packages/types';
import {
  OPTIONS_VALIDATION_MESSAGE,
  useMultipleOptionsValidation,
} from '../../../../../../hooks/selectPackage/useMultipleOptionsValidation';
import { UniqueIdentifier } from '../../../../../../../../types';

import { MuiStack } from '../../../../../../../../modules/material';
import CardWrapper from '../../shared/CardWrapper';
import SecondLevelCard from '../../shared/SecondLevelCard';
import Typography from '../../../../../../shared/library/Typography';
import SecondLevelListWrapper from '../../shared/SecondLevelListWrapper';
import CheckboxField from '../../../../../../shared/formElements/CheckboxField';
import Alert from '../../../../../../shared/library/Alert';
import ProductTooltipInfoIcon from '../../shared/TooltipInfoIcon';
import TitleWithInlineElement from '../../shared/TitleWithInlineElement';

export interface MultipleOptionsListProps {
  optionGroup: PricingProductOptionsGroup;
  index: number;
}

export default function MultipleOptionsList(props: MultipleOptionsListProps) {
  const {
    optionGroup: { max, min, options = [], productId, productOptionsTitle },
    index,
  } = props;
  const {
    formState: { errors },
  } = useFormContext<PricingPackagesFormValues>();
  const { t } = useTranslation();

  const fieldName = 'productsSelections.optionsMultipleMap';
  const productIdWithIndex = `${productId}-${index}`;
  const validationError =
    errors?.productsSelections?.optionsMultipleMap?.[productIdWithIndex];

  const selectedOptions =
    (useWatch<PricingPackagesFormValues>({
      name: `${fieldName}.${productIdWithIndex}`,
    }) as UniqueIdentifier[]) || [];

  const { listHeading, validationHandler } = useMultipleOptionsValidation({
    optionGroup: props.optionGroup,
  });

  return (
    <SecondLevelListWrapper
      heading={
        <Typography
          variant="subtitle2"
          neutralColorIndex={700}
          paletteColorName={
            validationError && !productOptionsTitle ? 'customError' : undefined
          }
          className="informa-payment-multiple-options__title"
        >
          {productOptionsTitle || listHeading}
        </Typography>
      }
      className="informa-payment-multiple-options"
    >
      <TargetToScroll name={`${fieldName}.${productIdWithIndex}`} />
      {validationError && (
        <Typography
          variant="smallText"
          neutralColorIndex={700}
          paletteColorName={'customError'}
          sx={{ mb: 1 }}
          className="informa-payment-multiple-options__validation"
        >
          {t(
            'site.pricing.products-selection.multiple-options-list.min-and-max.heading',
            {
              0: min,
              1: max,
            },
          )}
        </Typography>
      )}

      <MuiStack spacing={2}>
        {options.map((option) => {
          const isDisabled =
            !!max &&
            selectedOptions.length >= max &&
            !selectedOptions.includes(option.id);

          return (
            <CardWrapper
              paddingVariant="extraSmallTopBottom"
              enableBorder
              key={option.id}
              className="informa-payment-multiple-options__item"
              data-testid="multiple-option-list-item"
            >
              <CheckboxField<PricingPackagesFormValues>
                name={`${fieldName}.${productIdWithIndex}`}
                value={option.id}
                label={
                  <SecondLevelCard
                    title={
                      <TitleWithInlineElement
                        variant="featuredText"
                        title={option.name}
                        className="informa-payment-multiple-options-card__title"
                      >
                        {option.description && (
                          <ProductTooltipInfoIcon
                            text={option.description}
                            iconButtonSxProps={{ verticalAlign: 'bottom' }}
                          />
                        )}
                      </TitleWithInlineElement>
                    }
                    className="informa-payment-multiple-options-card"
                  />
                }
                rules={{
                  validate: validationHandler,
                }}
                nativeDisabled={isDisabled}
                multiple
                errorMessageDisabled
                labelOffset="small"
                sx={{ m: 0 }}
              />
            </CardWrapper>
          );
        })}

        {validationError &&
          validationError.message !== OPTIONS_VALIDATION_MESSAGE && (
            <Alert
              severity="error"
              className="informa-payment-multiple-options__error-message"
            >
              {validationError.message}
            </Alert>
          )}
      </MuiStack>
    </SecondLevelListWrapper>
  );
}
